import { DateTime } from 'luxon'
import { DiscountScopes, DiscountTypes, DiscountUnits } from '@invivodf/module-discount-sdk'
import { RequiredString } from './fields/validator/RequiredString'
import { RequiredValidityStartDate } from './fields/RequiredValidityStartDate'
import { NotRequiredValidityEndDate } from './fields/NotRequiredValidityEndDate'
import { RequiredAllowedString } from './fields/RequiredAllowedString'
import { MEASURE_UNITS } from '../constants/MEASURE_UNITS'
import { IDiscountFormProps } from './IDiscountFormProps'
import { NotRequiredPromoCode } from './fields/NotRequiredPromoCode'
import { NotRequiredSegment } from './fields/NotRequiredSegment'
import { RequiredProperty } from './fields/validator/RequiredProperty'
import { FormModes } from '../constants/FormModes'
import { NullableRequiredUses } from './fields/NullableRequiredUses'
import { NullableRequiredGroupSize } from './fields/NullableRequiredGroupSize'
import { FormErrors } from './FormErrors'

export abstract class BaseDiscountForm {
  public id?: string

  public isMeta: boolean

  public participants: number[]

  public reference: RequiredString

  public segment: NotRequiredSegment

  public validityStartDate: RequiredValidityStartDate

  public validityEndDate: NotRequiredValidityEndDate

  public internalName: RequiredString

  public publicName: RequiredString

  public measureUnit: RequiredAllowedString | null

  public unit: RequiredAllowedString

  public scope: DiscountScopes

  public promoCode: NotRequiredPromoCode

  public isFlash: RequiredProperty<boolean>

  public isSales: RequiredProperty<boolean>

  public nbOverallUsesMax: NullableRequiredUses

  public nbCustomerUsesMax: NullableRequiredUses

  public groupSize: NullableRequiredGroupSize

  public type: DiscountTypes

  public isExcludedAssociations: boolean

  protected constructor(props: IDiscountFormProps) {
    this.id = props.id
    this.isMeta = props.isMeta
    this.scope = props.policy.scope
    this.type = props.policy.type
    // eslint-disable-next-line
    this.participants = this.isMeta ? (props.participants ?? []) : [props.sellerId]
    this.reference = new RequiredString(props.reference || '', { errorMessage: FormErrors.ReferenceRequired })
    this.segment = new NotRequiredSegment(props.segment || null)
    this.internalName = new RequiredString(props.internalName || '', {
      errorMessage: FormErrors.InternalNameRequired,
    })
    this.publicName = new RequiredString(props.publicName || '', {
      errorMessage: FormErrors.PublicNameRequired,
    })
    this.unit = new RequiredAllowedString(props.unit || DiscountUnits.Euro, {
      errorMessage: FormErrors.UnitRequired,
      allowed: Object.values(DiscountUnits),
      allowedErrorMessage: FormErrors.UnitInvalid,
    })
    this.validityStartDate = new RequiredValidityStartDate(
      props.validityStartDate || DateTime.now().startOf('day').plus({ day: 1 }).toJSDate(),
    )
    this.validityEndDate = new NotRequiredValidityEndDate(props.validityEndDate || null)
    this.measureUnit =
      this.scope === DiscountScopes.Cart
        ? null
        : new RequiredAllowedString(props.measureUnit || '', {
            errorMessage: FormErrors.MeasureUnitRequired,
            allowed: Object.values(MEASURE_UNITS).map((unit) => unit.value),
            allowedErrorMessage: FormErrors.MeasureUnitInvalid,
          })
    this.promoCode = new NotRequiredPromoCode(props.promoCode || null)
    this.isFlash = new RequiredProperty<boolean>(props.isFlash || false, {
      errorMessage: FormErrors.IsFlashRequired,
    })
    this.isSales = new RequiredProperty<boolean>(props.isSales || false, {
      errorMessage: FormErrors.IsSalesRequired,
    })
    this.nbOverallUsesMax = new NullableRequiredUses(props.nbOverallUsesMax || null)
    this.nbCustomerUsesMax = new NullableRequiredUses(props.nbCustomerUsesMax || null)
    this.groupSize = new NullableRequiredGroupSize(props.groupSize || null)
    this.isExcludedAssociations = props.isExcludedAssociations || false
  }

  public get mode(): FormModes {
    return this.id ? FormModes.UPDATE : FormModes.CREATE
  }

  public isPromoCodeEditable(): boolean {
    return this.mode === FormModes.CREATE || this.isUpcoming()
  }

  public isPromoCodeOptional(): boolean {
    return !this.promoCode.validatedValue || this.mode === FormModes.CREATE
  }

  public hasGeneralInformationsErrors(): boolean {
    return (
      this.reference.failed ||
      this.validityStartDate.failed ||
      this.validityEndDate.failed ||
      this.internalName.failed ||
      this.publicName.failed ||
      (this.measureUnit ? this.measureUnit.failed : false) ||
      this.unit.failed ||
      this.isFlash.failed ||
      this.isSales.failed
    )
  }

  public hasPromoCodeErrors(): boolean {
    return this.promoCode.failed
  }

  public isDiscountWithAmount(): boolean {
    return [DiscountTypes.FixedPrice, DiscountTypes.Simple].includes(this.type)
  }

  public isSimpleDiscount(): boolean {
    return this.type === DiscountTypes.Simple
  }

  public isFixedPriceDiscount(): boolean {
    return this.type === DiscountTypes.FixedPrice
  }

  abstract isMeasureUnitSetToAnyAndNotEditable(): boolean

  abstract isMeasureUnitSetToUnitAndNotEditable(): boolean

  abstract isUnitSetToEuroAndNotEditable(): boolean

  abstract validate(): void

  public validateGeneralInformations(): void {
    this.reference.validate()
    this.validityStartDate.validate()
    this.validityEndDate.validate()
    this.internalName.validate()
    this.publicName.validate()
    if (this.measureUnit) this.measureUnit.validate()
    this.unit.validate()
    this.isFlash.validate()
    this.isSales.validate()
  }

  public validatePromoCode(): void {
    this.promoCode.validate()
  }

  public associationStepRequired(): boolean {
    return [DiscountScopes.Article, DiscountScopes.Group, DiscountScopes.Mixed_group].includes(this.scope)
  }

  public isUpcoming(): boolean {
    if (this.validityStartDate.validatedValue) {
      return this.validityStartDate.validatedValue > new Date()
    }
    return false
  }

  setMeta(value: boolean) {
    this.isMeta = value
  }

  setParticipants(participants: number[]) {
    if (this.isMeta) {
      this.participants = participants
    }
  }

  setIsExcludedAssociations(isExcluded: boolean) {
    this.isExcludedAssociations = isExcluded
  }
}
